import { BuildClass, Do, clamp, fsmData, } from '../../../universal';
import { React, _ } from '../../lib';
import { List } from './list';
import { getInternalValue, getValuesAfterClick } from './list-util';
import { ConditionalObject, useEffectCompare, useRSInstance, useStateSync, } from './meta-types';
import { Modal } from './modal';
import { stubDiv, stubInput, stubListInstance } from './stubs';
import { TextSearched, searchText } from './text';
const cbValAllS = '****all****';
const cbValNullS = '****null****';
const optionRowHeight = 21;
const flipHeightThreshold = 6 * optionRowHeight + 2;
const maxOptionsHeight = 19 * optionRowHeight + 2;
const ComboboxCmpt = (props, ref) => {
    const rs = useRSInstance({
        props: props,
        refs: {
            txt: React.useRef(stubInput),
            optionsList: React.useRef(stubListInstance),
            root: React.useRef(stubDiv),
        },
        defaultState: getDefaultStateFromProps,
        actionToDelta: getPartialStateFromAction,
    });
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            rs.refs.txt.current?.focus();
        },
        select: () => {
            rs.refs.txt.current?.select();
        },
        getElement: () => rs.refs.root.current,
    }));
    useStateSync({
        stateVal: rs.state.value,
        propVal: props.value,
        setState: v => {
            rs.dispatch([
                Action.ResetValueFromProps,
                {
                    value: getInternalValueCB(props, v),
                    text: getValueText(v, props),
                },
            ]);
        },
        setProp: v => {
            const isNumeric = arePropsNumeric(rs.props);
            const values = v.map(x => convertInternalToT(rs.props, x, isNumeric));
            if (props.multiple) {
                props.onUpdate(values);
            }
            else {
                props.onUpdate(_.first(values));
            }
        },
        compareFn: (p, s) => _.isEqual(s, getInternalValueCB(props, p)),
    });
    useEffectCompare(() => {
        rs.dispatch([Action.ResetText, { text: getValueText(props.value, props) }]);
    }, [props.options]);
    React.useEffect(() => {
        if (rs.state.fixedWidth != props.fixedWidth) {
            rs.dispatch([
                Action.ResetFixedWidthFromProps,
                { fixedWidth: props.fixedWidth },
            ]);
        }
    }, [rs, props.fixedWidth]);
    const evChange = React.useCallback((ev) => {
        rs.dispatch([Action.onUpdateText, { text: ev.target.value }]);
    }, [rs]);
    const evKeyDown = React.useCallback((ev) => {
        rs.dispatch([Action.OnKeyDown, { ev: ev }]);
    }, [rs]);
    const evClickBox = React.useCallback(() => {
        rs.dispatch([Action.OnClickBox]);
    }, [rs]);
    const evClickArrow = React.useCallback((ev) => {
        rs.dispatch([Action.OnClickArrow, { ev: ev }]);
    }, [rs]);
    const evFocus = React.useCallback((ev) => {
        rs.props.onFocus?.(ev);
        rs.dispatch([Action.OnFocus, { ev }]);
    }, [rs]);
    const evBlur = React.useCallback((ev) => {
        rs.props.onBlur?.(ev);
        requestAnimationFrame(() => {
            rs.dispatch([Action.OnBlur]);
        });
    }, [rs]);
    return (React.createElement("div", { ref: rs.refs.root, title: props.title, className: BuildClass({
            'ui5 ui5-combobox': true,
            [BuildClass(props.className)]: true,
            isActive: rs.state.isActive,
            isOpen: rs.state.isOpen,
            disabled: props.disabled ?? false,
            multiple: props.multiple ?? false,
        }), style: props.style },
        React.createElement("input", { className: "txt underlined", ref: rs.refs.txt, disabled: props.disabled ? true : undefined, placeholder: _.isEmpty(props.value) ? props.placeholder : '', tabIndex: props.tabIndex, autoFocus: props.autoFocus, autoComplete: "new-password", "data-lpignore": true, value: rs.state.text, onMouseDown: evClickBox, onChange: evChange, onKeyDown: evKeyDown, onFocus: evFocus, onBlur: evBlur }),
        React.createElement("span", { className: "arrow noselect underlined", onMouseDown: evClickArrow },
            React.createElement("img", { className: "arrow-img", src: "/static/img/svg/cb-arrow.svg" })),
        React.createElement(Modal, null,
            React.createElement("div", { className: BuildClass({
                    'ui5-combobox-options': true,
                    [BuildClass(props.className)]: true,
                    hidden: !rs.state.isOpen,
                    isFlippedUp: !rs.state.optionsPosition.isDown,
                    multiple: props.multiple ?? false,
                }), style: {
                    top: `${rs.state.optionsPosition.top}px`,
                    maxHeight: `${rs.state.optionsPosition.height}px`,
                    left: `${rs.state.optionsPosition.left}px`,
                    minWidth: `${rs.state.optionsPosition.minWidth}px`,
                    width: ConditionalObject(rs.state.fixedWidth != null, `${rs.state.fixedWidth}px`),
                } }, ConditionalObject(rs.state.isActive, () => buildOptionList(rs, evClickArrow))))));
};
const buildOptionList = (rs, evClickArrow) => {
    if (!rs.state.isActive) {
        return React.createElement(React.Fragment, null);
    }
    const isEditing = rs.state.isOpen && rs.state.text != '';
    const allOptions = getOptionsFull(rs.props, isEditing, rs.state.text);
    const allSelectableValues = fsmData(getOptionsFiltered(getOptions(rs.props), isEditing, rs.state.text), {
        filter: x => x.selectable ?? true,
        map: x => x.value,
    });
    const fullWidthCloseArrow = ConditionalObject(rs.props.multiple ?? false, React.createElement("div", { className: "close-arrow" },
        React.createElement("span", { className: "arrow noselect underlined", onMouseDown: evClickArrow },
            React.createElement("img", { className: "arrow-img", src: "/static/img/svg/cb-arrow.svg" }))));
    const rows = fsmData(allOptions, {
        filter: x => x.visible ?? true,
        map: item => ({
            value: item.value,
            selectable: item.selectableList,
            content: () => (React.createElement(ComboboxOptionCmpt, { dispatch: rs.dispatch, option: item, isEditing: isEditing, highlighted: (rs.props.multiple ?? false) &&
                    item.value === rs.state.highlighted, selected: Do(() => {
                    if (item.isSelectAll) {
                        return areAllSelectableSelected(allSelectableValues, rs.state.value);
                    }
                    else if (item.options) {
                        return areAllSelectableSelected(fsmData(item.options, {
                            filter: x => x.selectable,
                            map: x => x.value,
                        }), rs.state.value);
                    }
                    else if (rs.props.multiple) {
                        return rs.state.value.includes(item.value);
                    }
                    else if (item.value === cbValNullS &&
                        _.isEqual(rs.state.value, [])) {
                        return true;
                    }
                    return item.value === _.first(rs.state.value);
                }), searchText: rs.state.isOpen ? rs.state.text : '', indent: item.indent, showCheckbox: rs.props.multiple ?? false, isSelectAll: item.isSelectAll })),
        }),
    });
    const optionsListComponent = (React.createElement(List, { ref: rs.refs.optionsList, value: (rs.props.multiple ?? false)
            ? rs.state.highlighted
            : _.first(rs.state.value), onUpdate: value => {
            rs.dispatch([Action.onUpdateHighlighted, { value }]);
        }, multiple: false, disableFocusControl: true, disableClickHandlers: true, height: 21, rows: rows, vpHeight: rs.state.optionsPosition.height - (rs.props.multiple ? 21 : 0) }));
    return (React.createElement(React.Fragment, null,
        fullWidthCloseArrow,
        optionsListComponent));
};
const ComboboxOptionCmpt = (props) => {
    const selectable = !props.option.options
        ? props.option.selectable || props.option.isSelectAll
        : props.option.options.filter(x => x.selectable).length;
    const className = BuildClass({
        [BuildClass(props.option.className)]: true,
        selected: props.selected,
        highlight: props.highlighted,
        'no-select': !selectable,
        showAll: props.isSelectAll,
        [`indent-${props.indent}`]: !props.isEditing && props.indent != 0 && props.indent > 0,
    });
    const onClick = React.useCallback((ev) => {
        if (selectable) {
            props.dispatch([
                Action.ItemClicked,
                { ev: ev, value: props.option.value },
            ]);
        }
        else {
            ev.stopPropagation();
            ev.preventDefault();
        }
    }, [props, selectable]);
    return (React.createElement("div", { className: className, title: props.option.title ?? props.option.fullText, onMouseDown: onClick },
        ConditionalObject(props.showCheckbox, React.createElement("div", { className: "ui5 ui5-checkbox" },
            React.createElement("div", { className: BuildClass({
                    inner: true,
                    selected: props.selected,
                }) }))),
        React.createElement("span", null, props.option.display ? (props.option.display?.(props.searchText)) : (React.createElement(TextSearched, { text: props.isEditing ? props.option.fullText : props.option.text, needles: props.searchText.split(/\s+/).filter(x => x) })))));
};
var Action;
(function (Action) {
    Action[Action["ResetValueFromProps"] = 0] = "ResetValueFromProps";
    Action[Action["ResetFixedWidthFromProps"] = 1] = "ResetFixedWidthFromProps";
    Action[Action["onUpdateHighlighted"] = 2] = "onUpdateHighlighted";
    Action[Action["onUpdateText"] = 3] = "onUpdateText";
    Action[Action["OnKeyDown"] = 4] = "OnKeyDown";
    Action[Action["OnClickBox"] = 5] = "OnClickBox";
    Action[Action["OnClickArrow"] = 6] = "OnClickArrow";
    Action[Action["OnFocus"] = 7] = "OnFocus";
    Action[Action["OnBlur"] = 8] = "OnBlur";
    Action[Action["ItemClicked"] = 9] = "ItemClicked";
    Action[Action["ResetText"] = 10] = "ResetText";
})(Action || (Action = {}));
const getPartialStateFromAction = (rs, p) => {
    switch (p[0]) {
        case Action.ResetValueFromProps:
            return resetValueFromProps(rs, p[1].value, p[1].text);
        case Action.ResetFixedWidthFromProps:
            return { fixedWidth: p[1].fixedWidth };
        case Action.onUpdateHighlighted:
            return updateHighlighted(rs, p[1].value);
        case Action.onUpdateText:
            return updateText(rs, p[1].text);
        case Action.OnKeyDown:
            return onKeyDown(rs, p[1].ev);
        case Action.OnClickBox:
            return onClickBox(rs);
        case Action.OnClickArrow:
            return onClickArrow(rs, p[1].ev);
        case Action.OnFocus:
            return onFocus(rs, p[1].ev);
        case Action.OnBlur:
            return onBlur(rs);
        case Action.ItemClicked:
            return onItemClicked(rs, p[1].ev, p[1].value);
        case Action.ResetText:
            return resetTextFromProps(rs, p[1].text);
    }
};
const getDefaultStateFromProps = (props) => {
    const text = getValueText(props.value, props);
    return {
        value: getInternalValueCB(props, props.value),
        text: text,
        highlighted: null,
        canSendUpdates: false,
        isActive: false,
        isOpen: false,
        backspacePressed: false,
        optionsPosition: {
            left: 0,
            top: 0,
            height: _.min([props.maxHeight, maxOptionsHeight]) ?? maxOptionsHeight,
            width: 300,
            minWidth: 0,
            isDown: true,
        },
        fixedWidth: props.fixedWidth,
    };
};
const resetValueFromProps = (rs, value, text) => {
    if (rs.state.isOpen) {
        return { value };
    }
    return { value, text };
};
const resetTextFromProps = (rs, text) => {
    if (rs.state.isOpen) {
        return {};
    }
    return { text };
};
const updateHighlighted = (rs, value) => {
    requestAnimationFrame(() => {
        rs.refs.optionsList.current?.noop();
    });
    if (rs.props.multiple) {
        return { highlighted: value };
    }
    const newVal = [value];
    if (rs.state.isOpen) {
        return { value: newVal };
    }
    const isNumeric = arePropsNumeric(rs.props);
    return {
        value: newVal,
        text: getValueText(convertInternalToT(rs.props, newVal[0], isNumeric), rs.props),
    };
};
const updateText = (rs, text) => {
    if (rs.props.disabled) {
        return {};
    }
    refreshScrollJack(rs);
    const defaultChange = {
        ...ConditionalObject(!rs.state.isOpen, () => openBox(rs)),
        highlighted: null,
        text: text,
    };
    const options = getOptions(rs.props);
    const lower = (s) => s.toLowerCase();
    const matches_exact = options.filter(opt => lower(opt.text) === lower(text));
    if (matches_exact.length > 0) {
        const option = matches_exact[0];
        const isNumeric = arePropsNumeric(rs.props);
        const txt = getValueText(convertInternalToT(rs.props, option.value, isNumeric), rs.props);
        if (rs.props.multiple) {
            return {
                ...defaultChange,
                highlighted: option.value,
                text: txt,
            };
        }
        return {
            ...defaultChange,
            value: [option.value],
            text: txt,
        };
    }
    const selectionAtEnd = Do(() => {
        const [selStart, selEnd] = [
            rs.refs.txt.current.selectionStart,
            rs.refs.txt.current.selectionEnd,
        ];
        return selStart === selEnd && selStart === text.length;
    });
    if (!selectionAtEnd || rs.state.backspacePressed) {
        return defaultChange;
    }
    let index = 0;
    let suffix = '';
    const matches_prefix = fsmData(options, {
        filter: o => lower(o.text).startsWith(lower(text)),
        map: o => o.text,
    });
    const shortest_match_prefix = _.min(matches_prefix.map(t => t.length)) ?? 0;
    while (index < shortest_match_prefix) {
        const diffs = matches_prefix.map(t => lower(t[index] ?? ''));
        if (_.uniq(diffs).length > 1) {
            break;
        }
        index += 1;
    }
    suffix = matches_prefix[0]?.slice(text.length, index) ?? '';
    if (matches_prefix.length > 1) {
        suffix = lower(suffix);
    }
    if (suffix) {
        requestAnimationFrame(() => {
            rs.refs.txt.current.setSelectionRange(text.length, index);
        });
    }
    return {
        ...defaultChange,
        text: text + suffix,
    };
};
const onKeyDown = (rs, ev) => {
    const getSingleOptionSelectionDelta = () => {
        const isEditing = rs.state.isOpen && rs.state.text != '';
        const options = fsmData(getOptionsFull(rs.props, isEditing, rs.state.text), {
            filter: x => x.visible,
            map: x => x?.value ?? null,
        });
        const first = options[0];
        if (options.length != 1 || first == null) {
            return {};
        }
        const isNumeric = arePropsNumeric(rs.props);
        const val = convertInternalToT(rs.props, first, isNumeric);
        const vals = val == null ? null : [val];
        return {
            text: getValueText(vals, rs.props),
            ...simulateItemClick(rs, first, false),
        };
    };
    if ([33, 34, 35, 36, 38, 40].includes(ev.which)) {
        ev.preventDefault();
        ev.stopPropagation();
        const ts = ev.timeStamp;
        const evWhich = ev.which;
        const evShiftKey = ev.shiftKey;
        const opts = rs.refs.optionsList.current;
        requestAnimationFrame(() => {
            opts?.forwardEventKeyDown(ts, evWhich, evShiftKey);
        });
        return { backspacePressed: false };
    }
    if ([8, 46].includes(ev.which)) {
        return { backspacePressed: true };
    }
    if (ev.which == 27) {
        ev.preventDefault();
        ev.stopPropagation();
        return { backspacePressed: false, ...closeBox(rs) };
    }
    if (ev.which == 32 && rs.props.multiple && rs.state.highlighted) {
        ev.preventDefault();
        ev.stopPropagation();
        return {
            backspacePressed: false,
            ...simulateItemClick(rs, rs.state.highlighted, ev.shiftKey),
        };
    }
    if (ev.which == 9 && !rs.props.multiple) {
        return {
            backspacePressed: false,
            ...closeBox(rs),
            ...getSingleOptionSelectionDelta(),
        };
    }
    if (ev.which == 13) {
        ev.preventDefault();
        ev.stopPropagation();
        if (!rs.state.isOpen) {
            return openBox(rs);
        }
        if (rs.props.multiple) {
            const valChange = rs.state.highlighted
                ? simulateItemClick(rs, rs.state.highlighted, ev.shiftKey)
                : {};
            return {
                backspacePressed: false,
                ...valChange,
            };
        }
        const delta = {
            backspacePressed: false,
            ...closeBox(rs),
            ...getSingleOptionSelectionDelta(),
        };
        return delta;
    }
    return { backspacePressed: false };
};
const onClickBox = (rs) => {
    if (rs.props.disabled) {
        return {};
    }
    if (!rs.state.isOpen) {
        rs.refs.txt.current.focus();
        return openBox(rs);
    }
    return { highlighted: null };
};
const onClickArrow = (rs, ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (rs.props.disabled) {
        return {};
    }
    if (rs.state.isOpen) {
        return closeBox(rs);
    }
    rs.refs.txt.current.focus();
    return openBox(rs);
};
const onFocus = (rs, ev) => {
    if (rs.props.disabled) {
        return {};
    }
    rs.props.onFocus?.(ev);
    return openBox(rs);
};
const openBox = (rs) => {
    refreshScrollJack(rs);
    const rect = rs.refs.root.current.getBoundingClientRect();
    const opts = getOptionsFull(rs.props, false, rs.state.text);
    const offset = rs.props.multiple ? 24 : 2;
    const heightRaw = _.min([
        rs.props.maxHeight,
        maxOptionsHeight,
        optionRowHeight * opts.length + offset,
    ]) ?? 0;
    const height_down = clamp(window.innerHeight - rect.bottom, 0, heightRaw);
    const height_up = clamp(rect.top, 0, heightRaw);
    const isFlipped = height_down < flipHeightThreshold && height_up > height_down;
    const height = isFlipped ? height_up : height_down;
    const optionsPosition = {
        left: rect.left,
        top: isFlipped ? rect.top - height_up - 1 : rect.bottom - 1,
        width: rect.width,
        height: height,
        minWidth: rect.width,
        isDown: !isFlipped,
    };
    return {
        isActive: true,
        isOpen: true,
        text: '',
        optionsPosition: optionsPosition,
    };
};
const onBlur = (rs) => {
    if (rs.props.disabled) {
        return {};
    }
    return {
        ...closeBox(rs),
        isActive: false,
        highlighted: null,
    };
};
const closeBox = (rs) => {
    const isNumeric = arePropsNumeric(rs.props);
    const val = rs.state.value.map(v => convertInternalToT(rs.props, v, isNumeric));
    return {
        isOpen: false,
        text: getValueText(val.filter((value) => value != null), rs.props),
    };
};
const onItemClicked = (rs, ev, value) => {
    ev.stopPropagation();
    ev.preventDefault();
    const delta = simulateItemClick(rs, value, ev.shiftKey);
    if (rs.props.multiple) {
        return delta;
    }
    if (delta === undefined) {
        return {};
    }
    const isNumeric = arePropsNumeric(rs.props);
    const val = convertInternalToT(rs.props, _.first(delta.value) ?? cbValNullS, isNumeric);
    const vals = val == null ? [] : [val];
    return {
        ...delta,
        ...closeBox(rs),
        text: getValueText(vals, rs.props),
    };
};
const simulateItemClick = (rs, value, shiftKey) => {
    const options = getOptions(rs.props);
    const isEditing = rs.state.isOpen && rs.state.text != '';
    const allSelectableValues = fsmData(getOptionsFiltered(options, isEditing, rs.state.text), {
        filter: x => x.selectable ?? true,
        map: x => x.value ?? '',
    });
    if (rs.props.multiple && value === cbValAllS) {
        if (areAllSelectableSelected(allSelectableValues, rs.state.value)) {
            return {
                value: [],
                highlighted: cbValAllS,
            };
        }
        return {
            value: allSelectableValues,
            highlighted: cbValAllS,
        };
    }
    if (value.startsWith('__group-')) {
        if (!rs.props.multiple) {
            return {};
        }
        const option = _.first(getOptions(rs.props).filter(x => x.value == value));
        const childVals = fsmData(option?.options ?? [], {
            filter: x => x.selectable,
            map: x => x.value,
        });
        if (areAllSelectableSelected(childVals, rs.state.value)) {
            return {
                value: rs.state.value.filter(x => !childVals.includes(x)),
                highlighted: value,
            };
        }
        return {
            value: _.uniq([...rs.state.value, ...childVals]),
            highlighted: value,
        };
    }
    let modifier = 1;
    if (!rs.props.multiple) {
        modifier = 1;
    }
    else if (shiftKey) {
        modifier = 3;
    }
    else {
        modifier = 2;
    }
    const newValue = getValuesAfterClick(allSelectableValues, rs.state.value, value, modifier);
    return {
        value: newValue,
        highlighted: value,
    };
};
const getOptions = (props) => {
    const options = [];
    _.forEach(props.options, (item, index) => {
        if (!('options' in item)) {
            options.push({
                value: String(item.value),
                text: item.text,
                fullText: item.text,
                display: item.display,
                indent: 0,
                className: item.className ?? '',
                title: item.title ?? '',
                visible: item.visible ?? true,
                selectable: item.selectable ?? true,
                selectableList: item.selectable ?? true,
                isSelectAll: false,
                isNumber: _.isNumber(item.value),
                options: null,
            });
            return;
        }
        const prefix = item.prefix ?? item.text;
        const selectable = item.selectable ?? true;
        const visible = item.visible ?? true;
        const subOptions = _.map(item.options, subItem => ({
            value: String(subItem.value),
            text: subItem.text,
            fullText: `${prefix} → ${subItem.text}`,
            display: subItem.display,
            indent: 1,
            className: subItem.className ?? '',
            title: subItem.title ?? '',
            visible: visible && (subItem.visible ?? true),
            selectable: selectable && (subItem.selectable ?? true),
            selectableList: selectable && (subItem.selectable ?? true),
            isSelectAll: false,
            isNumber: _.isNumber(subItem.value),
            options: null,
        }));
        options.push({
            value: `__group-${index}`,
            text: item.text,
            fullText: item.text,
            display: null,
            indent: 0,
            className: BuildClass({
                [BuildClass(item.className)]: true,
                'group-heading': true,
            }),
            title: item.title ?? '',
            visible: item.visible ?? true,
            selectable: false,
            selectableList: Boolean(props.multiple) && (item.selectable ?? true),
            isSelectAll: false,
            isNumber: null,
            options: subOptions,
        });
        options.push(...subOptions);
    });
    return options;
};
const getOptionsFiltered = (optionsFlat, isEditing, text) => _.filter(optionsFlat, opt => {
    if (!isEditing) {
        return true;
    }
    if (!opt.selectable) {
        return false;
    }
    if (!text) {
        return true;
    }
    return searchText(opt.fullText, text.split(/\s+/), true);
});
const getOptionsFull = (props, isEditing, text) => {
    const optionsFlat = getOptions(props);
    const options = _.sortBy(getOptionsFiltered(optionsFlat, isEditing, text), opt => {
        if (!isEditing) {
            return 1;
        }
        const tl = (s) => s.toLowerCase();
        const prefix_match = tl(opt.text).startsWith(tl(text));
        return prefix_match ? 0 : 1;
    });
    const isFiltered = options.length !== optionsFlat.length;
    if (props.nullable != null && !isEditing) {
        options.unshift({
            value: cbValNullS,
            text: props.nullable,
            fullText: props.nullable,
            display: null,
            indent: 0,
            className: '',
            title: '',
            visible: true,
            selectable: true,
            selectableList: true,
            isSelectAll: false,
            isNumber: null,
            options: null,
        });
    }
    if (props.multiple && !props.noSelectAll && options.length !== 1) {
        const lbl = Do(() => {
            if (options.length === 0) {
                return 'No options meet this filter';
            }
            else if (isFiltered) {
                return 'Select Filtered';
            }
            return 'Select All';
        });
        options.unshift({
            value: cbValAllS,
            text: lbl,
            fullText: lbl,
            display: null,
            indent: 0,
            className: '',
            title: '',
            selectable: false,
            selectableList: true,
            visible: true,
            isSelectAll: true,
            isNumber: null,
            options: null,
        });
    }
    return options;
};
const getValueText = (val, props) => {
    const optLength = _.size(_.filter(getOptions(props), x => x.selectable));
    if (props.nullable && (val == null || val === cbValNullS || _.isEqual(val, []))) {
        return props.nullable;
    }
    if (!props.multiple) {
        const value = isValueMulti(val) ? _.first(val) : val;
        return fsmData(_.concat(getOptionsFull(props, false, '')), {
            filter: o => o.value === String(value),
            map: o => o?.text ?? '',
            takeFirst: true,
        });
    }
    const values = isValueMulti(val) ? val : [val];
    if (props.multiple && props.textView != null) {
        const textCalc = props.textView(values.filter(x => x != null), (values?.length ?? 0) === optLength);
        if (textCalc != null) {
            return textCalc;
        }
    }
    const isNumeric = arePropsNumeric(props);
    const matches = fsmData(getOptions(props), {
        filter: o => values.includes(convertInternalToT(props, o.value, isNumeric)),
        map: o => o.text,
    });
    if (optLength > 0 && matches.length === optLength) {
        return 'All Selected';
    }
    if (matches.length > 2) {
        return `${matches.length} Selected`;
    }
    return matches.join(', ');
};
const refreshScrollJack = (rs) => {
    requestAnimationFrame(() => {
        rs.refs.optionsList.current?.refreshScrollJack();
    });
};
const isValueMulti = (v) => _.isArray(v);
const areAllSelectableSelected = (selectable, selected) => _.isEqual(_.sortBy(selectable), _.sortBy(_.intersection(selectable, selected)));
const getInternalValueCB = (props, propVal) => {
    if (props.nullable && !props.nullable && propVal == null) {
        return [];
    }
    return getInternalValue(propVal).map(String);
};
const arePropsNumeric = (props) => {
    const firstDefined = _.find(getOptions(props), x => x.isNumber != null);
    return firstDefined?.isNumber ?? false;
};
const convertInternalToT = (props, val, isNumeric) => {
    if (props.nullable != null && !props.multiple && val === cbValNullS) {
        return null;
    }
    return isNumeric ? +val : val;
};
export const Combobox = React.forwardRef(ComboboxCmpt);
//# sourceMappingURL=combobox.js.map